import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { concat, Observable, of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { getSessionLink } from '../../helpers/coachee-log.utilities';
import { CoachingLogService } from '../../services/coaching-log/coaching-log.service';
import { LinkedItemsService } from '../../services/linked-items/linked-items.service';
import {
  LinkedItemParentTable,
  LinkedItemParentTableType,
  LinkedItemSearchOption,
  LinkedItemSearchSource,
  LinkedItemTargetTable,
} from '../../types/responses/linked-items.responses';
import { CreateSessionModalComponent } from '../create-session-modal/create-session-modal.component';

@Component({
  selector: 'app-linked-items-select',
  templateUrl: './linked-items-select.component.html',
  styleUrls: ['./linked-items-select.component.scss'],
  standalone: false,
})
export class LinkedItemsSelectComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('select') select: NgSelectComponent;

  @ViewChild('createSessionModal')
  createSessionModal: CreateSessionModalComponent;

  linkedItemList$: Observable<LinkedItemSearchOption[]>;

  isLoading = false;

  searchInput$ = new Subject<string>();

  @Input() placeholder = 'Search linked items...';

  @Input() emptyStateMessage =
    'Search for Coaching Sessions, Learning & Events, and Plans';

  @Input() label = 'Select linked items';

  @Input() isMulti = true;

  @Input() clearAfterSelection = true;

  @Input() preloadLinkedItems = true;

  @Input() targetTable: LinkedItemTargetTable;

  @Input() targetId: number;

  @Input() parentTableId: number;

  @Input() createSessionTooltip = '';

  @Input() sources?: LinkedItemSearchSource[];

  @Input() role?: string;

  @Input() includeE2l?: boolean;

  @Input() includeDistrictAdmins?: boolean;

  @Input() districtId?: number | null;

  @Output() readonly selectedLinkedItem =
    new EventEmitter<LinkedItemSearchOption | null>();

  @Output() readonly selectedLinkedItems = new EventEmitter<
    LinkedItemSearchOption[]
  >();

  labelId = uuidv4();

  internalSelectedLinkedItem: LinkedItemSearchOption | null = null;

  internalSelectedLinkedItemList: LinkedItemSearchOption[] = [];

  readonly LinkedItemParentTable = LinkedItemParentTable;

  get showCreateSessionButton(): boolean {
    return (
      !this.sources || this.sources.includes(LinkedItemSearchSource.COACHLOG)
    );
  }

  private readonly subscriptions = new Subscription();

  private sessionCreatedSub: Subscription | null = null;

  constructor(
    private linkedItemsService: LinkedItemsService,
    private coachingLogService: CoachingLogService
  ) {}

  ngOnInit(): void {
    if (this.shouldLoadLinkedItems()) {
      this.loadLinkedItems();
    }
    this.loadSearchableItems();
  }

  ngAfterViewInit(): void {
    if (!this.createSessionModal?.createSessionModal) {
      return;
    }

    this.subscriptions.add(
      this.createSessionModal.createSessionModal.isClosing.subscribe(() => {
        this.clearSessionCreatedSubscription();
      })
    );

    this.subscriptions.add(
      this.createSessionModal.createSessionModal.isDismissing.subscribe(() => {
        this.clearSessionCreatedSubscription();
      })
    );
  }

  ngOnDestroy(): void {
    this.sessionCreatedSub?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['targetId']?.currentValue !==
        changes['targetId']?.previousValue ||
      changes['targetTable']?.currentValue !==
        changes['targetTable']?.previousValue ||
      changes['preloadLinkedItems']?.currentValue !==
        changes['preloadLinkedItems']?.previousValue
    ) {
      if (this.shouldLoadLinkedItems()) {
        this.loadLinkedItems();
      } else {
        this.internalSelectedLinkedItemList = [];
        this.outputSelections();
      }
    }
  }

  private shouldLoadLinkedItems() {
    return !!(this.preloadLinkedItems && this.targetTable && this.targetId > 0);
  }

  loadLinkedItems() {
    this.linkedItemsService
      .getLinkedItems(this.targetTable, this.targetId)
      .subscribe((response) => {
        this.internalSelectedLinkedItemList = response.items;
        this.internalSelectedLinkedItem = null;
        this.outputSelections();
      });
  }

  clearSelectedLinkedItems() {
    this.internalSelectedLinkedItem = null;
    this.internalSelectedLinkedItemList = [];
    this.outputSelections();
  }

  reloadSelectedLinkedItemsFromTarget() {
    if (this.shouldLoadLinkedItems()) {
      this.loadLinkedItems();
      return;
    }

    this.clearSelectedLinkedItems();
  }

  linkedItemSelected(linkedItem: LinkedItemSearchOption | null) {
    if (!linkedItem) {
      return;
    }

    if (this.isMulti) {
      if (!this.alreadyAdded(linkedItem)) {
        this.internalSelectedLinkedItemList.push(linkedItem);
      }
      this.internalSelectedLinkedItem = null;
    } else {
      this.internalSelectedLinkedItem = linkedItem;
    }

    this.outputSelections(linkedItem);

    if (this.clearAfterSelection) {
      setTimeout(() => {
        this.internalSelectedLinkedItem = null;
      });
    }
  }

  removeLinkedItemFromList(linkedItem: LinkedItemSearchOption) {
    this.internalSelectedLinkedItemList =
      this.internalSelectedLinkedItemList.filter(
        (item) =>
          !(
            item.parent_table === linkedItem.parent_table &&
            item.parent_id === linkedItem.parent_id
          )
      );
    this.outputSelections();
  }

  alreadyAdded(linkedItem: LinkedItemSearchOption): boolean {
    return !!this.internalSelectedLinkedItemList.filter(
      (item) =>
        item.parent_table === linkedItem.parent_table &&
        item.parent_id === linkedItem.parent_id
    ).length;
  }

  private loadSearchableItems() {
    this.linkedItemList$ = concat(
      of([]),
      this.searchInput$.pipe(
        distinctUntilChanged(),
        debounceTime(300),
        tap(() => {
          this.isLoading = true;
        }),
        switchMap((term) => {
          const searchTerm = term?.trim();

          if (!searchTerm || searchTerm.length < 2) {
            this.isLoading = false;
            return of([]);
          }

          return this.linkedItemsService
            .searchItems(
              searchTerm,
              LinkedItemParentTable.IMPLEMENTATION_PLAN,
              this.parentTableId,
              this.sources ?? [
                LinkedItemSearchSource.COACHLOG,
                LinkedItemSearchSource.PD_SESSION,
                LinkedItemSearchSource.IMPLEMENTATION_PLAN,
              ],
              {
                role: this.role,
                include_e2l: this.includeE2l,
                include_district_admins: this.includeDistrictAdmins,
                district_id: this.districtId || undefined,
              }
            )
            .pipe(
              catchError(() => of([])),
              tap(() => {
                this.isLoading = false;
              })
            );
        })
      )
    );
  }

  private outputSelections(selectedItem: LinkedItemSearchOption | null = null) {
    this.selectedLinkedItem.emit(
      selectedItem || this.internalSelectedLinkedItem
    );
    this.selectedLinkedItems.emit(
      this.internalSelectedLinkedItemList.map((item) => {
        const payload: LinkedItemSearchOption = {
          parent_table: item.parent_table,
          parent_table_type:
            item.parent_table === LinkedItemParentTable.IMPLEMENTATION_PLAN
              ? null
              : item.parent_table_type,
          parent_table_type_label: item.parent_table_type_label ?? null,
          parent_id: item.parent_id,
          link_icon: item.link_icon,
          link_title: item.link_title,
        };

        if (item.parent_start_datetime !== undefined) {
          payload.parent_start_datetime = item.parent_start_datetime;
        }

        if (item.parent_end_datetime !== undefined) {
          payload.parent_end_datetime = item.parent_end_datetime;
        }

        if (item.link_url) {
          payload.link_url = item.link_url;
        }

        return payload;
      })
    );
  }

  canOpenLink(item: LinkedItemSearchOption): boolean {
    return (
      !!item.link_url && item.parent_table !== this.LinkedItemParentTable.SCHOOL
    );
  }

  shouldShowDate(item: LinkedItemSearchOption): boolean {
    return (
      item.parent_table !== this.LinkedItemParentTable.USER &&
      item.parent_table !== this.LinkedItemParentTable.SCHOOL
    );
  }

  getLinkedItemIcon(item: LinkedItemSearchOption): string {
    return (
      item.link_icon ||
      this.linkedItemsService.getDefaultIcon(item.parent_table)
    );
  }

  readonly shouldShowEndDate = (item: LinkedItemSearchOption): boolean =>
    item.parent_table !== this.LinkedItemParentTable.COACHLOG &&
    !!item.parent_end_datetime;

  openCreateSessionModal() {
    this.sessionCreatedSub?.unsubscribe();

    this.sessionCreatedSub =
      this.coachingLogService.coachlogCreatedWithSession.subscribe(
        (session) => {
          this.linkedItemSelected({
            parent_table: LinkedItemParentTable.COACHLOG,
            parent_table_type: session.type.view as LinkedItemParentTableType,
            parent_table_type_label: session.type.title,
            parent_id: session.id,
            parent_start_datetime: session.startDatetime,
            parent_end_datetime: session.endDatetime,
            link_icon: 'card-checklist',
            link_title: session.title,
            link_url:
              getSessionLink(session) ?? `/coaching/log/${session.id}/info`,
          });
          this.sessionCreatedSub?.unsubscribe();
          this.sessionCreatedSub = null;
        }
      );

    this.createSessionModal?.openModal();
  }

  private clearSessionCreatedSubscription() {
    // Wait a tick so successful session creation can emit before the modal close event clears state.
    setTimeout(() => {
      this.sessionCreatedSub?.unsubscribe();
      this.sessionCreatedSub = null;
    });
  }
}
